import { tWithKeyAttr as t } from "../../../i18nHelper";
import { useEffect, useState } from "react";
import loginStyles from "../../../pages/login/login.module.css";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { loginEmailValidationSchema } from "../../../utils/validation";
import {
  authApi,
  useLoginWithCodeMutation,
} from "../../../services/api/auth.api";
import {
  errorUserSelector,
  errorTimeStampSelector,
} from "../../../services/selectors/auth.selectors";
import { useAppSelector } from "../../../services/hooks";
import { useActions } from "../../../services/hooks/useActions";
import { IUser } from "../../../types/types";

function LoginEmailForm({ from }: { from: string }) {
  const navigate = useNavigate();
  const [login, { isLoading }] = authApi.useLoginMutation();
  const [loginWithCode] = useLoginWithCodeMutation();
  const loginError = useAppSelector(errorUserSelector);
  const retryAfterTimestamp = useAppSelector(errorTimeStampSelector);

  const { setPhone, setLoginInfo, clearRetryAfter } = useActions();

  const [countdown, setCountdown] = useState<number | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const handleLogin = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    const response = await login({ email, password });
    if ("data" in response && response?.data?.success) {
      const { user, accessToken } = response.data;
      if (accessToken) {
        navigate(from, { state: { from }, replace: true });
      } else {
        const u = user as IUser & { Id?: string | number; Phone?: string };
        const phoneNum = u.Phone ?? u.phone;
        const id = u.Id ?? u.id;
        setLoginInfo({ email, password, id });
        setPhone(phoneNum);
        const codeRes = await loginWithCode({
          phone: phoneNum,
          usersId: id,
        });
        if ("data" in codeRes && codeRes.data?.success) {
          navigate("enter_code", { state: { from } });
        }
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginEmailValidationSchema(),
    onSubmit: (values, { setSubmitting }) => {
      handleLogin(formik.values);
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (retryAfterTimestamp) {
      const interval = setInterval(() => {
        const secondsLeft = Math.max(
          0,
          Math.floor((retryAfterTimestamp - Date.now()) / 1000)
        );
        setCountdown(secondsLeft);

        if (secondsLeft <= 0) {
          clearInterval(interval);
          setCountdown(null);
          clearRetryAfter();
        }
      }, 1000);

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryAfterTimestamp]);

  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  return (
    <form onSubmit={formik.handleSubmit} className={loginStyles.login__form}>
      <input
        type="email"
        required={true}
        className={`${loginStyles.login__input} ${
          loginStyles.login__input_email
        } ${
          formik.touched.email && formik.errors.email
            ? loginStyles.login__input_invalid
            : ""
        }`}
        name="email"
        id="email"
        data-testid="email"
        placeholder={t("EMAIL_ADDRESS")}
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
      />
      <div className="inputContainer">
        <input
          type={passwordVisible ? "text" : "password"}
          required={true}
          className={`${loginStyles.login__input} ${
            formik.touched.password && formik.errors.password
              ? loginStyles.login__input_invalid
              : ""
          }`}
          name="password"
          id="password"
          data-testid="password"
          placeholder={t("PASSWORD_LABEL")}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        <button
          type="button"
          className={`passwordToggle ${
            passwordVisible && "passwordToggle_visible"
          }`}
          onClick={togglePasswordVisibility}
        />
      </div>
      {(formik.touched.email && formik.errors.email) ||
      (formik.touched.password && formik.errors.password) ? (
        <p className={loginStyles.login__invalid_message}>
          {formik.errors.email ?? formik.errors.password}
        </p>
      ) : null}
      {loginError && (
        <p className={loginStyles.login__invalid_message}>{loginError}</p>
      )}
      <button
        type="submit"
        disabled={
          (countdown !== null && countdown > 0) ||
          !formik.values.email ||
          !formik.isValid ||
          formik.isSubmitting ||
          isLoading
        }
        className={`button ${loginStyles.login__button} ${
          (countdown !== null && countdown > 0) ||
          !formik.values.email ||
          !formik.isValid ||
          formik.isSubmitting ||
          isLoading
            ? loginStyles.login__button_disabled
            : "hover"
        }`}
        data-testid="login-by-email"
      >
        {isLoading ? t("SENDING") : t("ENTRANCE")}{" "}
        {countdown !== null && countdown > 0 ? `(${countdown})` : ""}
      </button>
    </form>
  );
}

export default LoginEmailForm;
